import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  createStyles,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid/Grid';
import { useTranslation } from 'react-i18next';
import utils from '../../services/utils.service';
import { useRevolusendApi } from '../../context';
import notFoundImg from '../../assets/img/not-found.svg';
import Autocomplete from '@material-ui/lab/Autocomplete/Autocomplete';
import DetailsIcon from '@material-ui/icons/Info';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { Tenant } from '../../services/types/revolusend/tenant.type';
import { CryptoOrder } from '../../services/types/revolusend/cryptoOrder.type';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    img: {
      width: 400,
      maxWidth: '100%',
      display: 'block',
      margin: '0 auto',
    },
    noBottomBorder: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
  })
);

const Row = (props: { orderListItem: CryptoOrder, tenants: Tenant[] }) => {
  const { orderListItem: orderListItem, tenants } = props;
  const history = useHistory();
  const { t } = useTranslation(['cryptoOrders']);

  const getTransactionStatusLabel = () => {
    return orderListItem.status;
  }

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <a href={'/crypto/orders/' + orderListItem.id} target='_blank'>
            <IconButton size='small'>
              <DetailsIcon />
            </IconButton>
          </a>
        </TableCell>
        <TableCell>{orderListItem.id}</TableCell>
        <TableCell>
          {(() => {
            const tenantId = (orderListItem as any).tenant_id ?? (orderListItem as any).tenantId ?? (orderListItem as any).tenant?.id;
            const tenant = tenants?.find((x) => x.id === tenantId);
            return tenant?.name ?? '-';
          })()}
        </TableCell>
        <TableCell>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <a href={'/revolusend/transfers/' + orderListItem.transaction_id} target='_blank'>
              <IconButton size='small'>
                <DetailsIcon />
              </IconButton>
            </a>
            {orderListItem.transaction_id}
          </span>
        </TableCell>
        <TableCell>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <a href={'/revolusend/users/' + orderListItem.user_id} target='_blank'>
              <IconButton size='small'>
                <DetailsIcon />
              </IconButton>
            </a>
            {orderListItem.user_id}
          </span>
        </TableCell>
        <TableCell>
          {`${utils.currencyFormat(orderListItem.amount)}  ${orderListItem.coin_symbol}`}
        </TableCell>
        <TableCell>
          <Chip label={t(getTransactionStatusLabel())} color={['REQUESTED'].includes(orderListItem.status) ? 'default' : 'primary'} />
        </TableCell>
        <TableCell>
          {orderListItem.external_reference}
        </TableCell>
        <TableCell>
          {moment(orderListItem.created).format('DD-MM-YYYY HH:mm:ss')}
        </TableCell>
        <TableCell>
          {orderListItem.updated ? moment(orderListItem.updated).format('DD-MM-YYYY HH:mm:ss') : '--'}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export const CryptoOrders = () => {
  const { t } = useTranslation(['cryptoOrders']);
  const api = useRevolusendApi();
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [orders, setOrders] = React.useState<CryptoOrder[]>([]);
  const [tenants, setTenants] = React.useState<Tenant[]>([]);
  const [selectedTenant, setSelectedConsumer] = React.useState<Tenant | null>(null);
  const [reference, setReference] = React.useState('');
  const states = [
    'REQUESTED',
    'COMPLETED',
    'REFUND_REQUESTED',
    'REFUNDED',
  ];
  const [selectedStatus, selectStatus] = React.useState<string | null>(null);

  const [page, setPage] = React.useState(0);

  const limit = 15;

  const classes = useStyles();

  React.useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        const { crypto_orders, count } = await api.listCryptoOrders({
          tenantId: selectedTenant?.id,
          externalReference: reference,
          status: selectedStatus ? selectedStatus : undefined,
        }, limit, page * limit, 'desc');
        if (isMounted) {
          setOrders(crypto_orders);
          setCount(count);
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    }
  }, [selectedTenant, reference, selectedStatus, page]);

  React.useEffect(() => {
    utils.runAsync(async () => {
      setTenants(await api.listTenants());
    });
  }, []);

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedStatus, reference, selectedTenant]);

  const handlePageChangeCallback = React.useCallback((event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12}>
        <Grid item container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              onChange={(event, value) => {
                setSelectedConsumer(value);
              }}
              value={selectedTenant}
              options={tenants}
              autoHighlight
              disabled={loading}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderOption={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('SEARCH_TENANT')}
                  variant='standard'
                  fullWidth={true}
                  InputProps={{
                    ...params.InputProps,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  }}
                />
              )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              onChange={(event, value) => {
                selectStatus(value);
              }}
              value={selectedStatus}
              options={states}
              autoHighlight
              disabled={loading}
              getOptionLabel={(option) => t(option)}
              getOptionSelected={(option, value) => option === value}
              renderOption={(option) => t(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('SEARCH_STATUS')}
                  variant='standard'
                  fullWidth={true}
                  InputProps={{
                    ...params.InputProps,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  }}
                />
              )} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label={t('SEARCH_REFERENCE')}
              value={reference}
              disabled={loading}
              onChange={(e) => setReference(e.target.value)}
              variant='standard'
              fullWidth={true}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Grid container>

            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>{t('ID')}</TableCell>
                      <TableCell>{t('TENANT')}</TableCell>
                      <TableCell>{t('TRANSACTION_ID')}</TableCell>
                      <TableCell>{t('USER_ID')}</TableCell>
                      <TableCell>{t('AMOUNT')}</TableCell>
                      <TableCell>{t('STATUS')}</TableCell>
                      <TableCell>{t('EXTERNAL_REFERENCE')}</TableCell>
                      <TableCell>{t('CREATED')}</TableCell>
                      <TableCell>{t('UPDATED')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && orders.length === 0 &&
                      <TableCell colSpan={16}>
                        <Box padding={2}>
                          <Grid container justify='center' item xs={12}>
                            <CircularProgress />
                          </Grid>
                        </Box>
                      </TableCell>
                    }
                    {!loading && orders.length === 0 &&
                      <TableCell colSpan={16}>
                        <Box padding={2}>
                          <Typography align='center' variant='h5'>{t('NO_DATA')}</Typography>
                          <img className={classes.img} src={notFoundImg} />
                        </Box>
                      </TableCell>
                    }
                    {orders.map(o =>
                      <Row
                          orderListItem={o}
                          tenants={tenants}
                          key={o.id}
                        />)}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                rowsPerPageOptions={[limit]}
                count={count}
                rowsPerPage={limit}
                page={page}
                onPageChange={handlePageChangeCallback}
                onChangePage={handlePageChangeCallback}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid >
  )
}
